import React, { useState, useEffect } from "react";
import { FiMapPin, FiFileText } from "react-icons/fi";
import axios from "axios";
import { OutlinedInput } from "../../../../../../components/OutlinedInput";
import { PrimaryButton } from "../../../../../../components/PrimaryButton";
import { SecondaryButton } from "../../../../../../components/SecondaryButton";
import { showToast } from "../../../../../../components/CustomToast";
import { IAddressFormData, ILicenseeFormData } from "../../dtos.d";
import {
  SectionBlock,
  SectionHeader,
  SectionTitleWrapper,
  SectionTitleRow,
  SectionTitle,
  SectionSubtitle,
  InputGroup,
  Row,
  FlexInput,
  CheckboxLabel,
  SmoothCollapse,
  Footer,
} from "./styles";

const cepMask = (value: string) => {
  return value
    .replace(/\D/g, "")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .substring(0, 9);
};

interface StepAddressProps {
  addressFormData: IAddressFormData;
  setAddressFormData: React.Dispatch<React.SetStateAction<IAddressFormData>>;
  companyAddressFormData: IAddressFormData | undefined;
  setCompanyAddressFormData: React.Dispatch<React.SetStateAction<IAddressFormData | undefined>>;
  registerCompanyAddress: boolean;
  setRegisterCompanyAddress: React.Dispatch<React.SetStateAction<boolean>>;
  licenseeFormData: ILicenseeFormData;
  setLicenseeFormData: React.Dispatch<React.SetStateAction<ILicenseeFormData>>;
  nextStep: () => void;
  prevStep: () => void;
}

export function StepAddress({
  addressFormData,
  setAddressFormData,
  companyAddressFormData,
  setCompanyAddressFormData,
  registerCompanyAddress,
  setRegisterCompanyAddress,
  licenseeFormData,
  setLicenseeFormData,
  nextStep,
  prevStep,
}: StepAddressProps) {
  const [errors, setErrors] = useState<any>({});
  const [searchingCepMain, setSearchingCepMain] = useState(false);
  const [searchingCepBilling, setSearchingCepBilling] = useState(false);

  const [showMainAddressFields, setShowMainAddressFields] = useState(
    !!addressFormData.cep && addressFormData.cep.replace(/\D/g, "").length === 8
  );
  const [showBillingAddressFields, setShowBillingAddressFields] = useState(
    !!companyAddressFormData?.cep && companyAddressFormData.cep.replace(/\D/g, "").length === 8
  );

  // Initialize companyAddressFormData with template if null and register is true
  useEffect(() => {
    if (registerCompanyAddress && !companyAddressFormData) {
      setCompanyAddressFormData({
        country: addressFormData.country,
        address: "",
        number: "",
        complement: "",
        cep: "",
        district: "",
        state: "",
        city: "",
        type: { value: "other", optionText: "Outro" },
      });
    }
  }, [registerCompanyAddress, companyAddressFormData, setCompanyAddressFormData, addressFormData.country]);

  const handleMainChange = (field: keyof IAddressFormData, value: any) => {
    setAddressFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev: any) => ({ ...prev, [field]: "" }));
  };

  const handleBillingChange = (field: keyof IAddressFormData, value: any) => {
    if (companyAddressFormData) {
      setCompanyAddressFormData({ ...companyAddressFormData, [field]: value });
    }
    const billingField = `billing_${String(field)}`;
    if (errors[billingField]) setErrors((prev: any) => ({ ...prev, [billingField]: "" }));
  };

  const handleMainCepChange = async (value: string) => {
    const masked = cepMask(value);
    handleMainChange("cep", masked);

    const cleanCep = masked.replace(/\D/g, "");
    if (cleanCep.length === 8) {
      setSearchingCepMain(true);
      setShowMainAddressFields(true);
      try {
        const response = await axios.get(`https://viacep.com.br/ws/${cleanCep}/json/`);
        if (response.data && !response.data.erro) {
          setAddressFormData((prev) => ({
            ...prev,
            address: response.data.logradouro || "",
            district: response.data.bairro || "",
            city: response.data.localidade || "",
            state: response.data.uf || "",
          }));
          // Sync licensee city/state as well
          setLicenseeFormData((prev) => ({
            ...prev,
            city: response.data.localidade || prev.city,
            state: response.data.uf || prev.state,
          }));
          setErrors((prev: any) => ({
            ...prev,
            address: "",
            district: "",
            city: "",
            state: "",
          }));
        } else {
          showToast({ type: "error", message: "CEP não encontrado." });
        }
      } catch (err) {
        showToast({ type: "error", message: "Erro ao consultar o CEP." });
      } finally {
        setSearchingCepMain(false);
      }
    }
  };

  const handleBillingCepChange = async (value: string) => {
    const masked = cepMask(value);
    handleBillingChange("cep", masked);

    const cleanCep = masked.replace(/\D/g, "");
    if (cleanCep.length === 8) {
      setSearchingCepBilling(true);
      setShowBillingAddressFields(true);
      try {
        const response = await axios.get(`https://viacep.com.br/ws/${cleanCep}/json/`);
        if (response.data && !response.data.erro) {
          setCompanyAddressFormData((prev) => {
            if (!prev) return undefined;
            return {
              ...prev,
              address: response.data.logradouro || "",
              district: response.data.bairro || "",
              city: response.data.localidade || "",
              state: response.data.uf || "",
            };
          });
          setErrors((prev: any) => ({
            ...prev,
            billing_address: "",
            billing_district: "",
            billing_city: "",
            billing_state: "",
          }));
        } else {
          showToast({ type: "error", message: "CEP de faturamento não encontrado." });
        }
      } catch (err) {
        showToast({ type: "error", message: "Erro ao consultar o CEP." });
      } finally {
        setSearchingCepBilling(false);
      }
    }
  };

  const validateAndProceed = () => {
    const newErrors: any = {};

    // Validate main address
    if (!addressFormData.cep || addressFormData.cep.length < 9) {
      newErrors.cep = "CEP é obrigatório";
    }
    if (!addressFormData.address) {
      newErrors.address = "Endereço é obrigatório";
    }
    if (!addressFormData.noNumber && !addressFormData.number) {
      newErrors.number = "Número é obrigatório";
    }
    if (!addressFormData.district) {
      newErrors.district = "Bairro é obrigatório";
    }
    if (!addressFormData.city) {
      newErrors.city = "Cidade é obrigatória";
    }
    if (!addressFormData.state) {
      newErrors.state = "UF é obrigatória";
    }

    // Validate billing address if enabled
    if (registerCompanyAddress && companyAddressFormData) {
      if (!companyAddressFormData.cep || companyAddressFormData.cep.length < 9) {
        newErrors.billing_cep = "CEP de faturamento é obrigatório";
      }
      if (!companyAddressFormData.address) {
        newErrors.billing_address = "Endereço de faturamento é obrigatório";
      }
      if (!companyAddressFormData.noNumber && !companyAddressFormData.number) {
        newErrors.billing_number = "Número de faturamento é obrigatório";
      }
      if (!companyAddressFormData.district) {
        newErrors.billing_district = "Bairro de faturamento é obrigatório";
      }
      if (!companyAddressFormData.city) {
        newErrors.billing_city = "Cidade de faturamento é obrigatória";
      }
      if (!companyAddressFormData.state) {
        newErrors.billing_state = "UF de faturamento é obrigatória";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      showToast({
        type: "error",
        message: "Por favor, preencha todos os campos de endereço obrigatórios.",
      });
      return;
    }

    nextStep();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", width: "100%" }}>
      {/* BLOCO 1: ENDEREÇO PRINCIPAL E DE ENTREGA */}
      <SectionBlock>
        <SectionHeader>
          <SectionTitleWrapper>
            <SectionTitleRow>
              <SectionTitle>
                <FiMapPin /> Endereço Principal e de Entrega
              </SectionTitle>
            </SectionTitleRow>
            <SectionSubtitle>
              Digite o CEP para buscar automaticamente o endereço onde sua clínica opera e onde os materiais serão entregues.
            </SectionSubtitle>
          </SectionTitleWrapper>
        </SectionHeader>

        <InputGroup>
          <Row>
            <FlexInput flex={1}>
              <OutlinedInput
                inputName="cep"
                label={searchingCepMain ? "Buscando CEP..." : "CEP"}
                handleChange={handleMainCepChange}
                value={addressFormData.cep || ""}
                error={errors.cep}
              />
            </FlexInput>
            <FlexInput flex={2}>
              <OutlinedInput
                inputName="address"
                label="Endereço / Logradouro"
                handleChange={(val) => handleMainChange("address", val)}
                value={addressFormData.address || ""}
                error={errors.address}
                disabled={!showMainAddressFields}
              />
            </FlexInput>
          </Row>

          {showMainAddressFields && (
            <>
              <Row>
                <FlexInput>
                  <OutlinedInput
                    inputName="number"
                    label="Número"
                    handleChange={(val) => handleMainChange("number", val)}
                    value={addressFormData.noNumber ? "S/N" : addressFormData.number || ""}
                    error={errors.number}
                    disabled={!!addressFormData.noNumber}
                  />
                  <CheckboxLabel>
                    <input
                      type="checkbox"
                      checked={!!addressFormData.noNumber}
                      onChange={(e) => {
                        handleMainChange("noNumber", e.target.checked);
                        if (e.target.checked) handleMainChange("number", "");
                      }}
                    />
                    Não possui número
                  </CheckboxLabel>
                </FlexInput>

                <FlexInput>
                  <OutlinedInput
                    inputName="complement"
                    label="Complemento (opcional)"
                    handleChange={(val) => handleMainChange("complement", val)}
                    value={addressFormData.noComplement ? "Não possui" : addressFormData.complement || ""}
                    error={errors.complement}
                    disabled={!!addressFormData.noComplement}
                  />
                  <CheckboxLabel>
                    <input
                      type="checkbox"
                      checked={!!addressFormData.noComplement}
                      onChange={(e) => {
                        handleMainChange("noComplement", e.target.checked);
                        if (e.target.checked) handleMainChange("complement", "");
                      }}
                    />
                    Sem complemento
                  </CheckboxLabel>
                </FlexInput>
              </Row>

              <Row>
                <FlexInput flex={2}>
                  <OutlinedInput
                    inputName="district"
                    label="Bairro"
                    handleChange={(val) => handleMainChange("district", val)}
                    value={addressFormData.district || ""}
                    error={errors.district}
                  />
                </FlexInput>
                <FlexInput flex={2}>
                  <OutlinedInput
                    inputName="city"
                    label="Cidade"
                    handleChange={(val) => handleMainChange("city", val)}
                    value={addressFormData.city || ""}
                    error={errors.city}
                  />
                </FlexInput>
                <FlexInput flex={1}>
                  <OutlinedInput
                    inputName="state"
                    label="UF"
                    handleChange={(val) => handleMainChange("state", val)}
                    value={addressFormData.state || ""}
                    error={errors.state}
                  />
                </FlexInput>
              </Row>
            </>
          )}
        </InputGroup>
      </SectionBlock>

      {/* BLOCO 2: ENDEREÇO DE FATURAMENTO / FISCAL (OPCIONAL) */}
      <SectionBlock>
        <SectionHeader>
          <SectionTitleWrapper>
            <SectionTitleRow>
              <SectionTitle>
                <FiFileText /> Endereço de Faturamento / Fiscal (Opcional)
              </SectionTitle>
            </SectionTitleRow>
            <SectionSubtitle>
              Marque abaixo caso o endereço fiscal ou de cobrança da sua empresa seja diferente do endereço físico de entrega.
            </SectionSubtitle>
          </SectionTitleWrapper>
        </SectionHeader>

        <CheckboxLabel style={{ marginTop: 0 }}>
          <input
            type="checkbox"
            checked={registerCompanyAddress}
            onChange={(e) => setRegisterCompanyAddress(e.target.checked)}
          />
          <strong>Cadastrar endereço fiscal/faturamento diferente do endereço de entrega</strong>
        </CheckboxLabel>

        <SmoothCollapse visible={registerCompanyAddress}>
          <InputGroup>
            <Row>
              <FlexInput flex={1}>
                <OutlinedInput
                  inputName="billing_cep"
                  label={searchingCepBilling ? "Buscando CEP..." : "CEP de Faturamento"}
                  handleChange={handleBillingCepChange}
                  value={companyAddressFormData?.cep || ""}
                  error={errors.billing_cep}
                />
              </FlexInput>
              <FlexInput flex={2}>
                <OutlinedInput
                  inputName="billing_address"
                  label="Endereço / Logradouro Fiscal"
                  handleChange={(val) => handleBillingChange("address", val)}
                  value={companyAddressFormData?.address || ""}
                  error={errors.billing_address}
                  disabled={!showBillingAddressFields}
                />
              </FlexInput>
            </Row>

            {showBillingAddressFields && (
              <>
                <Row>
                  <FlexInput>
                    <OutlinedInput
                      inputName="billing_number"
                      label="Número"
                      handleChange={(val) => handleBillingChange("number", val)}
                      value={companyAddressFormData?.noNumber ? "S/N" : companyAddressFormData?.number || ""}
                      error={errors.billing_number}
                      disabled={!!companyAddressFormData?.noNumber}
                    />
                    <CheckboxLabel>
                      <input
                        type="checkbox"
                        checked={!!companyAddressFormData?.noNumber}
                        onChange={(e) => {
                          handleBillingChange("noNumber", e.target.checked);
                          if (e.target.checked) handleBillingChange("number", "");
                        }}
                      />
                      Não possui número
                    </CheckboxLabel>
                  </FlexInput>

                  <FlexInput>
                    <OutlinedInput
                      inputName="billing_complement"
                      label="Complemento"
                      handleChange={(val) => handleBillingChange("complement", val)}
                      value={companyAddressFormData?.noComplement ? "Não possui" : companyAddressFormData?.complement || ""}
                      error={errors.billing_complement}
                      disabled={!!companyAddressFormData?.noComplement}
                    />
                    <CheckboxLabel>
                      <input
                        type="checkbox"
                        checked={!!companyAddressFormData?.noComplement}
                        onChange={(e) => {
                          handleBillingChange("noComplement", e.target.checked);
                          if (e.target.checked) handleBillingChange("complement", "");
                        }}
                      />
                      Sem complemento
                    </CheckboxLabel>
                  </FlexInput>
                </Row>

                <Row>
                  <FlexInput flex={2}>
                    <OutlinedInput
                      inputName="billing_district"
                      label="Bairro"
                      handleChange={(val) => handleBillingChange("district", val)}
                      value={companyAddressFormData?.district || ""}
                      error={errors.billing_district}
                    />
                  </FlexInput>
                  <FlexInput flex={2}>
                    <OutlinedInput
                      inputName="billing_city"
                      label="Cidade"
                      handleChange={(val) => handleBillingChange("city", val)}
                      value={companyAddressFormData?.city || ""}
                      error={errors.billing_city}
                    />
                  </FlexInput>
                  <FlexInput flex={1}>
                    <OutlinedInput
                      inputName="billing_state"
                      label="UF"
                      handleChange={(val) => handleBillingChange("state", val)}
                      value={companyAddressFormData?.state || ""}
                      error={errors.billing_state}
                    />
                  </FlexInput>
                </Row>
              </>
            )}
          </InputGroup>
        </SmoothCollapse>
      </SectionBlock>

      {/* FOOTER DE AÇÕES */}
      <Footer>
        <SecondaryButton
          type="button"
          text="Voltar"
          onClick={prevStep}
          style={{ minWidth: "120px" }}
        />

        <PrimaryButton
          type="button"
          text="Avançar"
          onClick={validateAndProceed}
          disabled={!showMainAddressFields || (registerCompanyAddress && !showBillingAddressFields)}
          style={{ minWidth: "140px" }}
        />
      </Footer>
    </div>
  );
}
